import type { SpreadsheetColumnId } from '~/_shared/types/spreadsheetData/spreadsheetColumn';
import {
  mapMetricsToServerModel, type MetricModel,
} from '~/_shared/utils/metric/metrics.factory';
import { defaultBoundaryTerritoryGroupResponseSettings } from '~/boundary/settings/defaultBoundarySettings';
import { boundaryTerritoryGroupCreateSettingsStyleDefaults } from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import type { BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import {
  BoundaryTerritoryType,
  type CalculateBucketFunction,
  type CalculateBucketFunctionNumeric,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroup.type';
import { type boundaryTerritoryGroupsCreateRequest } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.actionCreators';
import {
  convertLabelConfigToServerModel, convertLabelStyleToServerModel, createBoundaryTerritoryCreateRequestFromBoundaryTerritoryGroupModel,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.factory';
import {
  type BoundaryBucketType, type BoundaryTerritoryGroupSettingsRequest,
  type BoundaryTerritoryStyleRequest,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.repository';
import {
  type BoundaryTerritoryGroup, type BoundaryTerritoryGroupConnectedEntityData,
  type BoundaryTerritoryLabelStyle,
  type BoundaryTerritoryLabelValueConfig,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

export type CreateBoundaryTerritoryGroupParams = {
  mapId: number;
  boundaryGroupId: BoundaryGroupId;
  territoryType: BoundaryTerritoryType;
  fillData?: SpreadsheetColumnId | null;
  demographicId?: string | null;
  numericCalculateBucketFunction?: CalculateBucketFunction | null;
  numberOfRanges?: number;
  connectedEntityData?: BoundaryTerritoryGroupConnectedEntityData;
  metrics?: ReadonlyArray<MetricModel>;
  style?: BoundaryTerritoryStyleRequest;
  disableMapInteraction?: boolean;
  hideLabels?: boolean;
  labelStyle?: BoundaryTerritoryLabelStyle;
  labelValueConfig?: BoundaryTerritoryLabelValueConfig;
};

const baseRequestFromBTG = (
  btg: BoundaryTerritoryGroup,
  defaultBaseSettings: BoundaryTerritoryGroupSettingsRequest,
): ReturnType<typeof createBoundaryTerritoryGroupRequestParams> => {
  const baseRequest = createBoundaryTerritoryCreateRequestFromBoundaryTerritoryGroupModel(btg);
  return {
    ...baseRequest,
    settings: {
      ...baseRequest.settings,
      bucket_type: defaultBaseSettings.bucket_type,
      ...defaultBaseSettings.boundary_territory_type ?
        { boundary_territory_type: defaultBaseSettings.boundary_territory_type } :
        {},
      ...defaultBaseSettings.calculate_bucket_function ?
        { calculate_bucket_function: defaultBaseSettings.calculate_bucket_function } :
        {},
      ranges_count: undefined,
      boundary_territories: undefined, // do not set to [], we want BE to restore previous previous value if BTG is restored and not created
      connected_entity_data: defaultBaseSettings.connected_entity_data,
    },
  };
};

export const createBoundaryTerritoryGroupRequestParams = (
  params: CreateBoundaryTerritoryGroupParams,
  baseBTG: BoundaryTerritoryGroup | null = null,
): Parameters<typeof boundaryTerritoryGroupsCreateRequest>[0] => {
  const {
    mapId,
    boundaryGroupId,
    territoryType,
    fillData,
    demographicId,
    numericCalculateBucketFunction,
    numberOfRanges,
    connectedEntityData,
    metrics,
    style,
    disableMapInteraction,
    hideLabels,
    labelStyle,
    labelValueConfig,
  } = params;

  const baseSettings = {
    ...defaultBoundaryTerritoryGroupResponseSettings,
    boundary_territory_type: territoryType,
    bucket_type: getBoundaryBucketTypeForBoundaryTerritoryType(territoryType) ?? undefined,
    style: style ?? boundaryTerritoryGroupCreateSettingsStyleDefaults,
    ranges_count: numberOfRanges,
    calculate_bucket_function: getCalculateBucketFunction(territoryType, numericCalculateBucketFunction),
    connected_entity_data: connectedEntityData,
    metrics: mapMetricsToServerModel(metrics ?? []),
    ...(disableMapInteraction && { disable_map_interaction: disableMapInteraction }),
    ...(hideLabels && { hide_labels: hideLabels }),
    ...(labelStyle && { boundary_territory_label_style: convertLabelStyleToServerModel(labelStyle) }),
    ...(labelValueConfig && { boundary_territory_label_config: convertLabelConfigToServerModel(labelValueConfig) }),
  };

  const baseRequest: ReturnType<typeof createBoundaryTerritoryGroupRequestParams> = baseBTG ?
    baseRequestFromBTG(baseBTG, baseSettings) :
    {
      map_id: mapId,
      boundary_group_id: boundaryGroupId,
      settings: baseSettings,
    };

  switch (territoryType) {
    case BoundaryTerritoryType.Numeric:
      if (fillData) {
        return {
          ...baseRequest,
          settings: {
            ...baseRequest.settings,
            bucket_column_id: fillData.columnId,
          },
        };
      }
      break;

    case BoundaryTerritoryType.Groups:
      if (fillData) {
        return {
          ...baseRequest,
          settings: {
            ...baseRequest.settings,
            bucket_column_id: fillData.columnId,
          },
        };
      }
      break;

    case BoundaryTerritoryType.Demographic:
      if (demographicId) {
        return {
          ...baseRequest,
          settings: {
            ...baseRequest.settings,
            demographic_id: demographicId,
          },
        };
      }
      break;

    case BoundaryTerritoryType.Density:
      return {
        ...baseRequest,
        settings: {
          ...baseRequest.settings,
        },
      };

    case BoundaryTerritoryType.Manual:
      return {
        ...baseRequest,
        settings: {
          ...baseRequest.settings,
        },
      };

    default:
      // Fallback for any unhandled territory type
      break;
  }

  return {
    ...baseRequest,
    settings: {
      ...baseRequest.settings,
    },
  };
};

export const getBoundaryBucketTypeForBoundaryTerritoryType = (territoryType: BoundaryTerritoryType): BoundaryBucketType | null => {
  switch (territoryType) {
    case BoundaryTerritoryType.Groups:
      return 'value';
    case BoundaryTerritoryType.Numeric:
    case BoundaryTerritoryType.Density:
      return 'value-range';
    case BoundaryTerritoryType.Demographic:
      return 'value-range';
    default:
      return null;
  }
};

const numericCalculateBucketFunctions = ['min', 'avg', 'max', 'sum'];
const isNumericBucketFunction = (f: CalculateBucketFunction | null): f is CalculateBucketFunctionNumeric =>
  !!f && !!numericCalculateBucketFunctions.find(i => i === f);

export const getNumericCalculateBucketFunction = (
  boundaryTerritoryGroup: BoundaryTerritoryGroup
): CalculateBucketFunctionNumeric | null => {
  if (boundaryTerritoryGroup.settings.boundaryTerritoryType !== BoundaryTerritoryType.Numeric) {
    return null;
  }

  const current = boundaryTerritoryGroup.settings.calculateBucketFunction;
  return isNumericBucketFunction(current) ? current : 'sum';
};

export const getCalculateBucketFunction = (
  territoryType: BoundaryTerritoryType,
  numericCalculateFunction?: CalculateBucketFunction | null,
): CalculateBucketFunction | null => {
  switch (territoryType) {
    case BoundaryTerritoryType.Groups:
      return 'equal';
    case BoundaryTerritoryType.Numeric:
      return numericCalculateFunction ?? 'sum';
    case BoundaryTerritoryType.Demographic:
    case BoundaryTerritoryType.Density:
      return 'sum';
    default:
      return null;
  }
};

export const hasBoundaryFillTypeBoundaryData = (boundaryTerritoryType: BoundaryTerritoryType | null): boolean => {
  const fillDataIsNotShown = [BoundaryTerritoryType.Manual, BoundaryTerritoryType.Density, null];

  return !fillDataIsNotShown.includes(boundaryTerritoryType);
};

export const areBoundaryLabelsVisible = (btg: BoundaryTerritoryGroup): boolean => !btg.settings.hideLabels || btg.settings.labelValueConfig.showValue;
